import React, { useState, useEffect, useCallback } from 'react';
import { Formik } from 'formik';
import { Form, Select, Checkbox } from 'formik-antd';
import { Modal, message, Spin, Descriptions, Collapse, Divider, AutoComplete } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Row from '~/components/Row';
import FormControl from '~/components/Form/FormControl';
import { ModalFooter } from '~/components/Modal';
import errorHandler from 'Utils/errorHandler';
import Addresses from '~/components/Addresses';
import Contacts from '~/components/Contact';
import OtherContactForm from '~/components/Contact/OtherContactForm';
import { TabAddress } from './styles';
import { customerCreateQb, getCustomerQb } from '~/services/hooks/quickbooksApi';
import { createCustomer, updateCustomer, findCustomerById } from '~/services/hooks/customers';
import { findAllTaxItems } from '~/services/hooks/taxes';
import debounce from 'lodash/debounce';
import { store } from '~/store';
import * as Yup from 'yup';

const initialValues = {
  id: null,
  company: null,
  primaryContactFirstName: '',
  primaryContactLastName: '',
  primaryContactJobTitle: '',
  primaryContactMainPhone: '',
  primaryContactMainEmail: '',
  primaryContactWorkPhone: '',
  primaryContactccEmail: '',
  primaryContactMobilePhone: '',
  secondaryContactFirstName: '',
  secondaryContactLastName: '',
  secondaryContactJobTitle: '',
  secondaryContactMainPhone: '',
  secondaryContactMainEmail: '',
  secondaryContactWorkPhone: '',
  secondaryContactccEmail: '',
  secondaryContactMobilePhone: '',
  website: '',
  faxNumber: '',
  other: '',
  companyName: '',
  checkPayByACH: false,
  checkPayByCheck: false,
  salesTaxCode: 'NON',
  taxItem: null,
  addresses: [],
};
const initValuesCContact = {
  id: null,
  primaryContact: null,
  secondaryContact: null,
  website: '',
  faxNumber: '',
  other: '',
};

const customerSchema = Yup.object().shape({
  companyName: Yup.string()
    .max(255)
    .required(),
  salesTaxCode: Yup.string().required(),
  primaryContactFirstName: Yup.string()
    .required()
    .max(25, 'Field must have at most 25 characters'),
  primaryContactLastName: Yup.string()
    .required()
    .max(25, 'Field must have at most 25 characters'),
  primaryContactJobTitle: Yup.string()
    .required()
    .max(40, 'Field must have at most 40 characters'),
  // addresses: Yup.array().of(addressSchema).min(1, 'At least one address is required'),
});

const { Option } = Select;
const { Panel } = Collapse;

export default function AccountForm({ visible, onClose, record }) {
  const { t } = useTranslation();
  const { id: recordId = null, flag: recordFlag = 'LEAD', qbId: recordQbId = null } = record || {};
  const [recordData, setRecordData] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  // const [customer, setAccounts] = useState(null);
  const [customerContact, setAccountContact] = useState(initValuesCContact);
  const [primaryContact, setPrimaryContact] = useState(null);
  const [secondaryContact, setSecondaryContact] = useState(null);
  const [taxItem, setTaxItem] = useState(null);
  const [company, setCompany] = useState(null);
  const [taxItems, setTaxItems] = useState([]);
  const [qbOptions, setQbOptions] = useState([]);
  const [loadingQb, setLoadingQb] = useState(false);
  const [selectedQb, setSelectedQb] = useState(null);

  // QuickBooks search
  const handleQbSearch = async value => {
    if (!value) {
      setQbOptions([]);
      return;
    }
    setLoadingQb(true);
    try {
      const resp = await getCustomerQb({ name: encodeURIComponent(value), job: false });
      const customers = resp?.QueryResponse?.Customer || [];
      const options = customers.map(c => ({
        label: c.FullyQualifiedName,
        value: c.Id,
        data: c,
      }));
      setQbOptions(options);
    } catch (err) {
      console.error(err);
    }
    setLoadingQb(false);
  };
  const debouncedQbSearch = useCallback(debounce(handleQbSearch, 500), [handleQbSearch]);

  // Busca dados do cliente e popula `recordData`
  const fetchRecords = async () => {
    setLoading(true);
    try {
      if (!recordId) {
        // setAccounts(null);
        setAccountContact(initValuesCContact);
        setPrimaryContact(null);
        setSecondaryContact(null);
        setTaxItem(null);
        setRecordData(initialValues);
      } else {
        // obtém dados do servidor ou do `record`
        const data = record && record.id ? record : await findCustomerById(recordId);

        // builds initialData from initialValues
        const initialData = { ...initialValues };
        initialData.id = data.id;
        initialData.company = data.company;
        initialData.companyName = data.companyName;
        initialData.checkPayByACH = data.checkPayByACH;
        initialData.checkPayByCheck = data.checkPayByCheck;
        initialData.salesTaxCode = data.salesTaxCode;
        initialData.taxItem = data.taxItem?.id || null;

        // injeta UUIDs e passa ao Formik
        initialData.addresses = data.addresses.map((addr, idx) => ({
          ...addr,
          uuid: idx + 1,
        }));

        // contatos
        if (data.customerContact) {
          const cc = data.customerContact;
          setAccountContact(cc);
          setPrimaryContact(cc.primaryContact);
          setSecondaryContact(cc.secondaryContact);
          initialData.primaryContactFirstName = cc.primaryContact.firstName;
          initialData.primaryContactLastName = cc.primaryContact.lastName;
          initialData.primaryContactJobTitle = cc.primaryContact.jobTitle;
          initialData.primaryContactMainPhone = cc.primaryContact.mainPhone;
          initialData.primaryContactMainEmail = cc.primaryContact.mainEmail;
          initialData.primaryContactWorkPhone = cc.primaryContact.workPhone;
          initialData.primaryContactccEmail = cc.primaryContact.ccEmail;
          initialData.primaryContactMobilePhone = cc.primaryContact.mobilePhone;

          if (cc.secondaryContact) {
            initialData.secondaryContactFirstName = cc.secondaryContact.firstName;
            initialData.secondaryContactLastName = cc.secondaryContact.lastName;
            initialData.secondaryContactJobTitle = cc.secondaryContact.jobTitle;
            initialData.secondaryContactMainPhone = cc.secondaryContact.mainPhone;
            initialData.secondaryContactMainEmail = cc.secondaryContact.mainEmail;
            initialData.secondaryContactWorkPhone = cc.secondaryContact.workPhone;
            initialData.secondaryContactccEmail = cc.secondaryContact.ccEmail;
            initialData.secondaryContactMobilePhone = cc.secondaryContact.mobilePhone;
          }

          initialData.website = cc.website;
          initialData.faxNumber = cc.faxNumber;
          initialData.other = cc.other;
        } else {
          setAccountContact(initValuesCContact);
          setPrimaryContact(null);
          setSecondaryContact(null);
        }

        // setAccounts(data);
        setTaxItem(data.taxItem);
        setRecordData(initialData);
      }
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const fetchCompany = async () => {
    setLoading(true);
    const { profile } = store.getState().user;
    setCompany(profile.company);
    setLoading(false);
  };

  const fetchTaxItems = async () => {
    setLoading(true);
    try {
      const data = await findAllTaxItems();
      setTaxItems(data);
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      fetchCompany();
      fetchRecords();
      fetchTaxItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, recordId]);

  const handleSave = async (values, { setErrors }) => {
    setLoading(true);
    try {
      // 1) decide qual qbId usar
      let qbIdToSave = recordQbId;
      if (!qbIdToSave) {
        if (selectedQb?.Id) {
          qbIdToSave = selectedQb.Id;
        } else {
          const customerDTO = {
            PrimaryEmailAddr: { Address: customerContact.primaryContact?.mainEmail },
            DisplayName: values.companyName,
            Job: false,
          };
          const data = await customerCreateQb(customerDTO);
          qbIdToSave = data.Id;
        }
      }

      // 2) monta payload usando recordId, recordFlag e qbIdToSave
      const payload = {
        id: recordId,
        company: recordId ? values.company : company,
        customerContact,
        companyName: values.companyName,
        qbId: qbIdToSave,
        checkPayByACH: values.checkPayByACH,
        checkPayByCheck: values.checkPayByCheck,
        salesTaxCode: values.salesTaxCode,
        taxItem: taxItem ? taxItem.id : null,
        addresses: values.addresses,
        flag: recordFlag,
      };

      // 3) chama update ou create
      if (recordId) {
        await updateCustomer(payload);
        message.success(t('messages:customerSuccessEdit'));
      } else {
        await createCustomer(payload);
        message.success(t('messages:customerSuccess'));
      }
      onClose();
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (contact, primary) => {
    const cc = { ...customerContact };
    if (primary) {
      cc.primaryContact = contact;
      setPrimaryContact(contact);
    } else {
      cc.secondaryContact = contact;
      setSecondaryContact(contact);
    }
    setAccountContact(cc);
  };

  // opções de salesTaxCode
  const salesTaxCodes = [
    { id: 1, code: 'TAX', description: t('screens:accounts.data.salesTaxCodes.tax') },
    { id: 2, code: 'NON', description: t('screens:accounts.data.salesTaxCodes.non') },
  ];

  return (
    <Formik
      initialValues={recordData}
      enableReinitialize
      onSubmit={handleSave}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={customerSchema}
    >
      {({ errors, isSubmitting, submitForm, resetForm, values, setFieldValue }) => {
        function handlePrimary(contact) {
          handleSelect(contact, true);
          setFieldValue('primaryContactFirstName', contact.firstName);
          setFieldValue('primaryContactLastName', contact.lastName);
          setFieldValue('primaryContactJobTitle', contact.jobTitle);
        }

        function handleSecondary(contact) {
          handleSelect(contact, false);
          setFieldValue('secondaryContactFirstName', contact.firstName);
          setFieldValue('secondaryContactLastName', contact.lastName);
          setFieldValue('secondaryContactJobTitle', contact.jobTitle);
        }

        const getAddresses = type => (
          <Addresses
            addressType={type}
            addresses={values.addresses}
            onHandleAddresses={newList => setFieldValue('addresses', newList)}
          />
        );
        // tabs de endereços
        const tabItems = [
          { label: 'Billing Address', key: 'billing', children: getAddresses('BILLING') },
          { label: 'Shipping Address', key: 'shipping', children: getAddresses('SHIPPING') },
        ];

        const genExtra = key => {
          switch (key) {
            case '1':
              return primaryContact ? (
                <DeleteOutlined
                  onClick={e => {
                    e.stopPropagation();
                    handleSelect(null, true);
                  }}
                />
              ) : null;
            case '2':
              return secondaryContact ? (
                <DeleteOutlined
                  onClick={e => {
                    e.stopPropagation();
                    handleSelect(null, false);
                  }}
                />
              ) : null;
            case '3':
              return (
                <EditOutlined
                  onClick={e => {
                    e.stopPropagation();
                    setShowForm(true);
                  }}
                />
              );
            default:
              return null;
          }
        };

        return (
          <Modal
            title={t('screens:accounts.btnNew')}
            width={800}
            onCancel={onClose}
            afterClose={resetForm}
            open={visible}
            loading={loading || isSubmitting}
            footer={
              <ModalFooter
                onOk={submitForm}
                loading={loading || isSubmitting}
                onCancel={onClose}
                cancelColor="default"
              />
            }
          >
            <Spin spinning={loading || isSubmitting}>
              <Form>
                {/* ==== Geral ==== */}
                <Row>
                  <Divider orientation="left"> {t('screens:accounts.data.generalContactInformation')}</Divider>
                </Row>
                <Row>
                  <FormControl
                    error={errors.companyName}
                    field="companyName"
                    label={t('screens:accounts.data.companyName')}
                    required
                  >
                    <AutoComplete
                      style={{ width: '100%' }}
                      options={qbOptions}
                      onSearch={debouncedQbSearch}
                      onSelect={(val, opt) => {
                        if (opt) {
                          setSelectedQb(opt.data);
                          setFieldValue('companyName', opt.label);
                        }
                      }}
                      onChange={v => {
                        setSelectedQb(null);
                        setFieldValue('companyName', v);
                      }}
                      notFoundContent={loadingQb ? <Spin size="small" /> : null}
                      placeholder={t('screens:reports.customerSearchPlaceholder')}
                      value={values.companyName}
                    />
                  </FormControl>
                </Row>

                {/* ==== Contatos ==== */}
                <br />
                <Collapse defaultActiveKey={['1']} className="ant-collapse-content-inactive">
                  <Panel header="Primary Contact" key="1" extra={genExtra('1')}>
                    <Contacts
                      primary
                      onSelect={c => {
                        handleSelect(c, true);
                        setFieldValue('primaryContactFirstName', c.firstName);
                        setFieldValue('primaryContactLastName', c.lastName);
                        setFieldValue('primaryContactJobTitle', c.jobTitle);
                      }}
                      onChange={c => handlePrimary(c)}
                      contact={primaryContact}
                    />
                  </Panel>
                  <Panel header="Secondary Contact" key="2" extra={genExtra('2')}>
                    <Contacts
                      primary={false}
                      onSelect={c => handleSelect(c, false)}
                      onChange={c => handleSecondary(c)}
                      contact={secondaryContact}
                    />
                  </Panel>
                  <Panel header="Others" key="3" extra={genExtra('3')}>
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label={t('screens:accounts.data.website')}>
                        {customerContact.website}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('screens:accounts.data.faxNumber')}>
                        {customerContact.faxNumber}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('screens:accounts.data.other')}>
                        {customerContact.other}
                      </Descriptions.Item>
                    </Descriptions>
                  </Panel>
                </Collapse>

                {/* ==== Pagamento ==== */}
                <br />
                <Divider orientation="left"> {t('screens:accounts.data.paymentInformation')}</Divider>
                <Row>
                  <FormControl field="checkPayByACH" cols={{ xs: 24 }}>
                    <div style={{ paddingTop: 0 }}>
                      <Checkbox value="checkPayByACH" name="checkPayByACH" />
                      <span style={{ marginLeft: 10 }}>{t('screens:customers.data.checkPayByACH')}</span>
                    </div>
                  </FormControl>
                  <FormControl field="checkPayByCheck" cols={{ xs: 24 }}>
                    <div style={{ paddingTop: 0 }}>
                      <Checkbox value="checkPayByCheck" name="checkPayByCheck" />
                      <span style={{ marginLeft: 10 }}>{t('screens:customers.data.checkPayByCheck')}</span>
                    </div>
                  </FormControl>
                </Row>

                {/* ==== Impostos ==== */}
                <br />
                <Divider orientation="left"> {t('screens:accounts.data.taxInformation')}</Divider>
                <Row>
                  <FormControl
                    field="salesTaxCode"
                    error={errors.salesTaxCode}
                    cols={{ xs: 6 }}
                    label={t('screens:accounts.data.salesTaxCode')}
                    required
                  >
                    <Select name="salesTaxCode" placeholder="Select">
                      {salesTaxCodes.map(item => (
                        <Option key={item.id} value={item.code}>
                          {item.description}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl field="taxItem" cols={{ xs: 12 }} label={t('screens:accounts.data.taxItem')}>
                    <Select name="taxItem" placeholder="Select">
                      {taxItems.map(item => (
                        <Option key={item.id} value={item.id}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                </Row>

                {/* ==== Endereços ==== */}
                <br />
                <span
                  style={{
                    color: '#600',
                    fontSize: 12,
                    fontStyle: 'italic',
                    opacity: 0.9,
                  }}
                >
                  {errors.addresses}
                </span>
                <TabAddress
                  size="small"
                  defaultActiveKey="billing"
                  type="card"
                  items={tabItems}
                  style={errors.addresses ? { border: '1px solid red' } : {}}
                />
              </Form>

              <OtherContactForm
                contact={customerContact}
                visible={showForm}
                onClose={() => setShowForm(false)}
                onChange={cc => {
                  setAccountContact(cc);
                  setShowForm(false);
                }}
              />
            </Spin>
          </Modal>
        );
      }}
    </Formik>
  );
}
